// components/RHFPasswordField.tsx
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { GeneralFormProps } from '@/types/general';

import { Button } from '../ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';

type Props = {
  showVisibilityToggle?: boolean;
  size?: 'sm' | 'default' | 'lg' | null | undefined;
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
} & GeneralFormProps;

function RHFPasswordField({
  name,
  title,
  description,
  helperText,
  placeholder,
  value: externalValue,
  onChange: externalOnChange,
  required,
  disabled,
  size = 'default',
  showVisibilityToggle = true,
}: Props) {
  const { control } = useFormContext<Record<string, string>>();
  const [showPassword, setShowPassword] = useState(false);
  const resolvedHelperText = helperText ?? description;

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const hasTitle = title !== undefined && title !== '';
  function buildFloatingLabel(): string | undefined {
    if (hasTitle === false) {
      return undefined;
    }
    if (required === true) {
      return `${title} *`;
    }
    return title;
  }
  const floatingLabel = buildFloatingLabel();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="w-full">
          <FormControl>
            <div className="relative">
              <Input
                {...field}
                inputSize={size}
                variant={fieldState.error !== undefined ? 'error' : 'default'}
                disabled={disabled}
                startIcon="lucide:lock"
                required={required}
                data-testid={field.name}
                type={showPassword === true ? 'text' : 'password'}
                label={floatingLabel}
                placeholder={placeholder}
                onChange={(e) => {
                  const value = String(e.target.value);
                  const newEvent = { ...e, target: { ...e.target, value } };

                  if (externalOnChange) {
                    externalOnChange(newEvent);
                    return;
                  }
                  field.onChange(newEvent);
                }}
                value={field.value ?? externalValue ?? ''}
              />
              {showVisibilityToggle === true && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  startIcon={showPassword === true ? 'lucide:eye-off' : 'lucide:eye'}
                  className="absolute right-0.5 top-1/2 -translate-y-1/2 hover:bg-transparent text-muted-foreground z-10"
                  onClick={togglePasswordVisibility}
                  disabled={disabled}
                />
              )}
            </div>
          </FormControl>
          {resolvedHelperText !== undefined && resolvedHelperText !== '' && fieldState.error === undefined && (
            <FormDescription className="mt-1.5 type-body-s text-on-surface-variant">
              {resolvedHelperText}
            </FormDescription>
          )}
          <FormMessage className="mt-1.5 type-body-s text-error font-medium" />
        </FormItem>
      )}
    />
  );
}

export default RHFPasswordField;
